import { useEffect } from 'react';

interface SEOData {
  title: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: string;
}

export function useSEO(data: SEOData) {
  useEffect(() => {
    document.title = data.title;

    const updateMeta = (name: string, content?: string) => {
      if (!content) return;
      let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateOg = (property: string, content?: string) => {
      if (!content) return;
      let meta = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    const updateCanonical = (href?: string) => {
      if (!href) return;
      let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    const updateJsonLd = (json?: string) => {
      if (!json) return;
      const existing = document.querySelector('script[id="page-json-ld"]');
      if (existing) {
        existing.remove();
      }
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('id', 'page-json-ld');
      script.textContent = json;
      document.head.appendChild(script);
    };

    updateMeta('description', data.description);
    updateMeta('keywords', data.keywords);
    updateCanonical(data.canonical);
    updateOg('og:title', data.ogTitle || data.title);
    updateOg('og:description', data.ogDescription || data.description);
    updateOg('og:type', data.ogType || 'website');
    updateOg('og:image', data.ogImage);
    updateJsonLd(data.jsonLd);

    return () => {
      const ld = document.querySelector('script[id="page-json-ld"]');
      if (ld) ld.remove();
    };
  }, [data.title, data.description, data.keywords, data.canonical, data.ogTitle, data.ogDescription, data.ogType, data.ogImage, data.jsonLd]);
}